import { Link as MuiLink, Typography } from '@mui/material'
import { History } from 'history'
import React from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom-v5-compat'

import TotpGuard from '@/app/component/guard/totp'
import CardLayout from '@/app/component/layout/card'
import ErrorLayout from '@/app/component/layout/error'
import Layout from '@/app/component/layout/page'
import { PAGE } from '@/app/definitions'
import { AuthGuard, AuthHeader } from '@/app/module/auth'
import Contacts from '@/app/module/contacts'
import UploadContactsDialog from '@/app/module/contacts/upload-contacts-dialog'
import Notifications from '@/app/module/notifications'
import { MatchProps } from '@/app/types'

type Props = {
  history: History
  match: MatchProps<{
    filterType: string
  }>
  name: string
}

const ContactsPage: React.FC<Props> = ({ name, match, history }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const pageParams = params.get('page')
  const page = pageParams && pageParams !== '0' ? parseInt(pageParams, 10) : 1
  const activeFilter =
    params.get('filterType') && params.get('filterId')
      ? {
          name: params.get('filterType') ?? '',
          value: params.get('filterId') ?? '',
        }
      : undefined

  const handleExit = React.useCallback(() => {
    if (location.key === 'default') {
      navigate('/contacts')
      return
    }
    navigate(-1)
  }, [location.key, navigate])

  if (params.get('filterType') && !['groups', 'segments'].find((type) => type === params.get('filterType'))) {
    return (
      <Layout title="Error - engageSPARK">
        <AuthHeader currentPage={PAGE.CONTACTS.FILTER} />
        <CardLayout>
          <ErrorLayout status={404}>
            <Typography>{`The filter "${match.params.filterType}" does not exist.`}</Typography>
            <Typography>
              We can help find your way back{' '}
              <MuiLink component={Link} to="/">
                home
              </MuiLink>
            </Typography>
          </ErrorLayout>
        </CardLayout>
      </Layout>
    )
  }

  return (
    <AuthGuard url={match.url} path={match.path}>
      <Layout disableScrolling title="Contacts - engageSPARK">
        <AuthHeader currentPage={name} />
        <TotpGuard>
          <Contacts
            activeFilter={activeFilter}
            addToGroupContacts={params.get('addToGroupContacts') ?? undefined}
            history={history}
            page={page}
            pageSearchTerm={params.get('search') ?? undefined}
          />
          <UploadContactsDialog open={name === PAGE.CONTACTS.UPLOAD} onClose={handleExit} />
        </TotpGuard>
      </Layout>
      <Notifications />
    </AuthGuard>
  )
}

export default ContactsPage
